// src/pages/Marketplace/surge-pricing/EventsCalendar.tsx
import React from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import type { SurgeEvent } from '@/pages/Marketplace/shared';

const locales = { 'en-US': enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

function tierColor(multiplier: number): string {
  if (multiplier <= 1.5) return '#4b5563';      // gray-600
  if (multiplier < 3)    return '#7c3aed';      // violet-600
  if (multiplier < 4)    return '#ec4899';      // pink-500
  return '#dc2626';                             // red-600
}

interface CalendarEvent { title: string; start: Date; end: Date; resource: SurgeEvent; }

interface Props {
  events: SurgeEvent[];
  onSelect: (e: SurgeEvent) => void;
  onCreateAt: (day: Date) => void;
}

export default function EventsCalendar({ events, onSelect, onCreateAt }: Props): React.ReactElement {
  const calEvents: CalendarEvent[] = events
    .filter((e) => !e.deleted_at)
    .map((e) => ({
      title: `${e.name} · ${e.multiplier}×`,
      start: new Date(e.starts_at),
      end: new Date(e.ends_at),
      resource: e,
    }));

  return (
    <div className="rbc-dark border border-gray-800 rounded-lg p-2">
      <Calendar
        localizer={localizer}
        events={calEvents}
        startAccessor="start"
        endAccessor="end"
        style={{ height: 560 }}
        views={['month']}
        defaultView="month"
        onSelectEvent={(ev) => onSelect(ev.resource)}
        onSelectSlot={(slot) => onCreateAt(slot.start as Date)}
        selectable
        eventPropGetter={(ev) => ({
          style: {
            backgroundColor: tierColor(ev.resource.multiplier),
            border: 'none',
            color: 'white',
            fontSize: 11,
          },
        })}
      />
      {/* inline dark overrides — scope via .rbc-dark */}
      <style>{`
        .rbc-dark .rbc-month-view { background:#0b0b12; color:#e5e7eb; border-color:#1f2937 }
        .rbc-dark .rbc-header, .rbc-dark .rbc-day-bg, .rbc-dark .rbc-month-row { border-color:#1f2937 }
        .rbc-dark .rbc-off-range-bg { background:#111827 }
        .rbc-dark .rbc-today { background:rgba(124,58,237,0.15) }
        .rbc-dark .rbc-toolbar button { color:#d1d5db; background:#111827; border-color:#1f2937 }
        .rbc-dark .rbc-toolbar button:hover { background:#1f2937 }
        .rbc-dark .rbc-toolbar-label { color:#fff }
      `}</style>
    </div>
  );
}
